import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Problem from '../components/Problem';
import Projects from '../components/Projects';
import Faq from '../components/Faq';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import { useSeo } from '../hooks/useSeo';
import { AUTOMATION_PATH, PROJECTS_PATH, WEBSITES_PATH } from '../routes';
import '../components/Hero.css';
import '../components/Services.css';
import './LandingPage.css';
import './ServicePage.css';

// One entry per service page. `proof` narrows client work server-side to the
// matching category (module-level objects, so Projects gets stable filters);
// `demos` adds the demo shortlist. Copy lives in servicePages.<key>.
const SERVICES = {
  websites: {
    proof: { category: 'web' },
    demos: true,
    related: { key: 'automation', to: AUTOMATION_PATH }
  },
  automation: {
    proof: { category: 'data' },
    demos: false,
    related: { key: 'websites', to: WEBSITES_PATH }
  }
};

/*
 * A landing page per service, for searches the home page can't rank for on
 * its own. Same register as the home page: the reader's problem first, then
 * what fixes it, then proof. The long-form article is filled from the
 * author's own notes; until it has sections, it doesn't render.
 */
function ServicePage({ service }) {
  const ns = `servicePages.${service}`;
  const config = SERVICES[service];
  useSeo(`${ns}.seo`);
  const { t } = useTranslation();
  const offer = Object.keys(t(`${ns}.offer.items`, { returnObjects: true }) || {});
  const sections = t(`${ns}.article.sections`, { returnObjects: true });
  const article = Array.isArray(sections) ? sections : [];

  const scrollToContact = (e) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="landing-page">
      <Navigation />

      <header id="top" className="hero">
        <p className="section-eyebrow">{t(`${ns}.eyebrow`)}</p>
        <h1 className="hero-title">
          <Trans i18nKey={`${ns}.title`} components={{ em: <em /> }} />
        </h1>
        <p className="hero-desc">{t(`${ns}.desc`)}</p>
        <a href="#contact" className="hero-cta" onClick={scrollToContact}>{t(`${ns}.cta`)}</a>
        <p className="hero-note">{t('hero.note')}</p>
      </header>

      <Problem ns={`${ns}.problem`} />

      <section className="services">
        <p className="section-eyebrow">{t(`${ns}.offer.eyebrow`)}</p>
        <h2 className="services-title">
          <Trans i18nKey={`${ns}.offer.heading`} components={{ em: <em /> }} />
        </h2>
        <div className="services-grid service-offer">
          {offer.map(key => (
            <article key={key} className="service-card">
              <h3 className="service-title">{t(`${ns}.offer.items.${key}.title`)}</h3>
              <p className="service-body">{t(`${ns}.offer.items.${key}.body`)}</p>
            </article>
          ))}
        </div>
      </section>

      <Projects group="production" facets={[]} fixedFilters={config.proof} copyKey={`${ns}.proof`} />

      {config.demos && (
        <Projects group="demo" facets={[]} featured moreLink={PROJECTS_PATH} copyKey={`${ns}.demos`} />
      )}

      {article.length > 0 && (
        <article className="service-article">
          <p className="section-eyebrow">{t(`${ns}.article.eyebrow`)}</p>
          <h2 className="service-article-title">
            <Trans i18nKey={`${ns}.article.heading`} components={{ em: <em /> }} />
          </h2>
          {article.map(section => (
            <section key={section.heading} className="service-article-section">
              <h3>{section.heading}</h3>
              {(section.paragraphs || []).map(text => <p key={text}>{text}</p>)}
            </section>
          ))}
        </article>
      )}

      <Faq ns={`${ns}.faq`} />

      <nav className="service-related" aria-label={t('servicePages.seeAlso')}>
        <span className="section-eyebrow">{t('servicePages.seeAlso')}</span>
        <Link to={config.related.to}>{t(`servicePages.${config.related.key}.eyebrow`)} →</Link>
        <Link to={PROJECTS_PATH}>{t('navigation.allProjects')} →</Link>
      </nav>

      <Contact />
      <Footer />
    </div>
  );
}

export default ServicePage;
